import GroupCard from "features/newMarketplace/GroupCard";
import styled, { css, useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import { useSelector } from "react-redux";
import {
  marketplaceSelectors,
  selectMarketplaceLoadingStates,
} from "@shared/slices/marketplace.slice";
import { LanguageNameSpaces, MarketplaceSubGroup } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Panel from "common/NewLook/Panel.tsx";
import NewLookGroupCard from "features/newMarketplace/NewLook/NewLookGroupCard";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import React, { useMemo } from "react";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { GROUP_CARD_HEIGHT } from "features/newMarketplace/common/constants.ts";
import GroupCardSkeleton from "features/newMarketplace/NewLook/GroupCardSkeleton.tsx";

interface TopTradersProps {
  openScoreModal?: (group: MarketplaceSubGroup) => void;
  showFullList?: boolean;
}

const TopTraders = ({ openScoreModal, showFullList }: TopTradersProps) => {
  const selector = useMemo(
    () => marketplaceSelectors.selectTopTraders(showFullList),
    [showFullList]
  );
  const topTraders = useSelector(selector);
  const loadingStatesSelector = useMemo(
    () => selectMarketplaceLoadingStates(showFullList),
    [showFullList]
  );
  const { loading } = useSelector(loadingStatesSelector);
  const { isNewLook } = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);

  if (isNewLook) {
    if (!topTraders.length && !loading) return null;
    // One panel with the top channels side by side, split by hairlines.
    return (
      <Panel title={t("top_rated.title")} flush={true}>
        <Featured aria-busy={loading || undefined}>
          {topTraders.length
            ? topTraders.map((group, index) => (
                <NewLookGroupCard
                  key={group.group_id}
                  group={group}
                  isTopTrader={true}
                  openScoreModal={openScoreModal}
                  index={index}
                  featured={true}
                />
              ))
            : [0, 1, 2].map((index) => <GroupCardSkeleton key={index} />)}
        </Featured>
      </Panel>
    );
  }

  return (
    <LoadingSpinnerOverlay
      isLoading={loading}
      wrap={true}
      wrapStyle={{ display: "flex", width: "100%", justifyContent: "center" }}
    >
      <Wrapper>
        {topTraders.map((group, index) => {
          return (
            <GroupCard
              key={group.group_id}
              group={group}
              isTopTrader={true}
              openScoreModal={openScoreModal}
              index={index}
            />
          );
        })}
      </Wrapper>
    </LoadingSpinnerOverlay>
  );
};

export default TopTraders;

const Wrapper = styled(Flex)`
  width: 100%;
  justify-content: center;
  max-width: 1230px;
  padding: 40px 0;
  column-gap: 16px;
  min-height: ${GROUP_CARD_HEIGHT}px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      align-items: stretch;
      gap: 16px;
      max-width: none;
      padding: 0;
      min-height: 0;
    `}
`;

const Featured = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  > * + * {
    border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    > * + * {
      border-left: 0;
      border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    }
  }
`;
